<script setup lang="ts">
import { Button } from '@/components/ui/button';
import SearchableSelectMenu from '@/components/ui/searchable-select-menu/SearchableSelectMenu.vue';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import type { MessageAttachment, Project } from '~/types/models';

const props = withDefaults(defineProps<{ open: boolean; attachment?: MessageAttachment | null; projects?: Project[]; saving?: boolean }>(), {
    attachment: null,
    projects: () => [],
    saving: false,
});
const emit = defineEmits<{ 'update:open': [value: boolean]; confirm: [projectId: string] }>();
const selectedProjectId = ref('');
const options = computed(() => props.projects.map((project) => ({
    value: project.id,
    label: project.name || 'Untitled Project',
    description: project.status || '',
})));

watch(() => props.open, (open) => {
    if (open) selectedProjectId.value = '';
});
</script>

<template>
    <Sheet :open="open" @update:open="$emit('update:open', $event)">
        <SheetContent side="right" class="!w-full gap-0 p-0 sm:!max-w-md">
            <SheetHeader class="border-b border-border/60 px-6 py-5 text-left">
                <SheetTitle>Add to Project files</SheetTitle>
                <SheetDescription>Reuse {{ attachment?.fileName || 'this attachment' }} in a Project without uploading it again.</SheetDescription>
            </SheetHeader>
            <div class="flex-1 space-y-2 px-6 py-5">
                <label class="text-sm font-medium">Project</label>
                <SearchableSelectMenu v-model="selectedProjectId" :options="options" variant="form" placeholder="Choose a Project" search-placeholder="Search Projects..." empty-state-label="No Projects found for this Client." show-all-by-default :disabled="saving" />
            </div>
            <SheetFooter class="border-t border-border/60 px-6 py-4">
                <Button variant="outline" :disabled="saving" @click="$emit('update:open', false)">Cancel</Button>
                <Button :disabled="saving || !selectedProjectId" @click="$emit('confirm', selectedProjectId)">{{ saving ? 'Adding…' : 'Add to Project' }}</Button>
            </SheetFooter>
        </SheetContent>
    </Sheet>
</template>
